import type { IdentifiedOrDeletedAuthor, ThreadAuthor } from "./author";

/**
 * How to render an author chip (PRTL-1385).
 *
 * The author chip union (`ThreadAuthor` / `IdentifiedOrDeletedAuthor`) is the
 * only identity shape the UI receives, and until now every component
 * re-derived the same five-way decision from it by hand — which avatar, which
 * initials, whether there is a profile to link to, whether a superuser sees
 * the real name behind "Anonymous". Two of those copies drifted into leaks
 * (PRTL-1384). This is the one place the rules live; the per-app
 * `AuthorAvatar` / `AuthorName` components only render what it says.
 */
export type AuthorAvatarSource =
    /** A real user: their photo if they have one, else seeded initials. */
    | { kind: "user"; url: string | null; initials: string }
    /** The anonymous mask — also for the viewer's own anonymous post, whose
     *  name already carries "(Anonymous)"; showing their photo next to it
     *  would defeat the point on a shared screen. */
    | { kind: "anonymous" }
    | { kind: "compass-team" }
    | { kind: "deleted" };

export interface AuthorPresentation {
    /** The union variant, for the rare caller that still needs to branch. */
    type: ThreadAuthor["type"];
    /** Display name as the server formatted it ("Deleted User", "Anonymous", "(Inactive)" …). */
    name: string;
    avatar: AuthorAvatarSource;
    /** A real user's photo, or null (no photo, or not a real user). For the
     *  few places that need a bare image URL rather than an AuthorAvatar. */
    photoUrl: string | null;
    /**
     * The profile to link to, or null when there is none: only an identified
     * author who has access to this surface. Never link anything else.
     */
    profileId: number | null;
    /** Identified but without access to this surface: rendered in italics. */
    isInactive: boolean;
    isCurrentUser: boolean;
    /** Only present for a superuser looking at someone else's anonymous content. */
    realName: string | null;
    /** PRTL-1348: the org an identified author acts under on this surface, if any. */
    organizationName: string | null;
}

/**
 * Which portal the chip is rendered in. It decides which access flag counts:
 * on a social surface (posts, feed, social comments, friends) an author
 * without social access is inactive and unlinked; on a private surface
 * (discussions, DMs, documents, reports) the same holds for private access.
 * Required, so no call site inherits the wrong portal by default.
 */
export type AuthorSurface = "social" | "private";

export interface DescribeAuthorOptions {
    surface: AuthorSurface;
}

export function describeAuthor(
    author: ThreadAuthor | IdentifiedOrDeletedAuthor,
    { surface }: DescribeAuthorOptions,
): AuthorPresentation {
    switch (author.type) {
        case "identified": {
            const hasAccess =
                surface === "social" ? author.isSocialEnabled : author.isPrivateEnabled;
            return {
                type: author.type,
                name: author.name,
                avatar: { kind: "user", url: author.avatarUrl, initials: author.initials },
                photoUrl: author.avatarUrl,
                profileId: hasAccess ? author.id : null,
                isInactive: !hasAccess,
                isCurrentUser: author.isCurrentUser,
                realName: null,
                organizationName: author.organizationName ?? null,
            };
        }
        case "anonymous-self":
            return {
                type: author.type,
                name: author.name,
                avatar: { kind: "anonymous" },
                photoUrl: null,
                profileId: null,
                isInactive: false,
                isCurrentUser: true,
                realName: null,
                organizationName: null,
            };
        case "anonymous":
            return {
                type: author.type,
                name: author.name,
                avatar: { kind: "anonymous" },
                photoUrl: null,
                profileId: null,
                isInactive: false,
                isCurrentUser: false,
                realName: author.realName ?? null,
                organizationName: null,
            };
        case "compass-team":
            return {
                type: author.type,
                name: author.name,
                avatar: { kind: "compass-team" },
                photoUrl: null,
                profileId: null,
                isInactive: false,
                isCurrentUser: author.isCurrentUser,
                realName: null,
                organizationName: null,
            };
        case "deleted":
            return {
                type: author.type,
                name: author.name,
                avatar: { kind: "deleted" },
                photoUrl: null,
                profileId: null,
                isInactive: false,
                isCurrentUser: false,
                realName: null,
                organizationName: null,
            };
    }
}
